import { FormControl, useToast } from "@chakra-ui/react";
import { format } from "date-fns";
import FormField from "../../../components/fields/FormField";
import FormModal from "../../../components/FormModal";
import { translate } from "../../../i18n";
import { CoachAvailabilityDto } from "../../../models/coach/dto/coach-availability.dto";
import { useDeleteCoachAvailability } from "../hooks/useDeleteCoachAvailability";

type Props = {
    isModalVisible: boolean;
    setIsModalVisible: (newValue: boolean) => void;
    onCoachAvailabilityDeleted: (id: string) => void;
    selectedAvailability: CoachAvailabilityDto;
    /**
     * Mirrors the API's `hasCoachSelfOrOrganizationAdminAccess` on
     * `DELETE coachs/availabilities/:id`: an org ADMIN may delete any coach's
     * availability, a COACH only their own. The parent already computes this
     * for the sibling `SetCoachAvailabilitiesModal`; without it here, a coach
     * viewing a COLLEAGUE's calendar got a Supprimer button that 403s — and
     * because it works on their own calendar, the failure looked random.
     */
    canManage?: boolean;
};
export default function SelectedCoachAvailabilityModal({ selectedAvailability, isModalVisible, setIsModalVisible, onCoachAvailabilityDeleted, canManage = true }: Props) {
    const toast = useToast();
    const { mutateAsync: deleteAvailability, isLoading: isDeleting } = useDeleteCoachAvailability(selectedAvailability?.coachId, (availabilityId) => {
        onCoachAvailabilityDeleted(availabilityId);
        setIsModalVisible(false);
    });

    if (!selectedAvailability) return null;

    return (
        <FormModal
            isOpen={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            title={translate('courts.availabilities')}
            onSubmit={() => setIsModalVisible(false)}
            submitLabel={translate('close')}
            size="3xl"
            destructiveAction={
                canManage
                    ? {
                          label: translate('delete'),
                          onClick: () => {
                              deleteAvailability(selectedAvailability.id).catch(() => {
                                  toast({ title: translate('error'), status: 'error', duration: 3000, position: 'top-right' });
                              });
                          },
                          isLoading: isDeleting,
                      }
                    : undefined
            }
        >
            <FormControl>
                <FormField
                    label={translate('event.start_date')}
                    value={format(new Date(selectedAvailability.startTime), "dd MMMM yyyy HH:mm")}
                    isDisabled
                    onChange={() => {}}
                />
                <FormField
                    label={translate('event.end_date')}
                    value={format(new Date(selectedAvailability.endTime), "dd MMMM yyyy HH:mm")}
                    isDisabled
                    onChange={() => {}}
                />
            </FormControl>
        </FormModal>
    )
}
